import { useEffect, useState } from "react";
import { apiGet } from "../../lib/apiClient";

/**
 * Reads availability directly from room_inventory:
 *  - room_type (text): single | twin | triple | quadruple | campervan_pitch
 *  - total (int)
 *  - booked (int)
 *
 * Returns:
 *  {
 *    remaining: { r1, r2, r3, r4, campervan_pitch },
 *    loading,
 *    error
 *  }
 */
export function useRoomAvailability({ enabled = true } = {}) {
  const EMPTY = { r1: 0, r2: 0, r3: 0, r4: 0, campervan_pitch: 0 };

  const [remaining, setRemaining] = useState(EMPTY);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!enabled) return;

      setLoading(true);
      setError("");

      try {
        const { data } = await apiGet("/.netlify/functions/booking-room-inventory");

        if (cancelled) return;

        const byType = {};
        for (const row of data || []) {
          const type = String(row.room_type || "").toLowerCase().trim();
          const total = Number(row.total || 0);
          const booked = Number(row.booked || 0);
          const rem = Math.max(0, total - booked);
          byType[type] = rem;
        }

        setRemaining({
          r1: byType.single ?? 0,
          r2: byType.twin ?? 0,
          r3: byType.triple ?? 0,
          r4: byType.quadruple ?? 0,
          campervan_pitch: byType.campervan_pitch ?? 0,
        });
      } catch (e) {
        if (cancelled) return;
        setError(e?.message || "Failed to load availability");
        setRemaining(EMPTY);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { remaining, loading, error };
}
